import React, {FormEvent} from 'react';
import {
  ContactMessage,
  CreateUserPayload,
  PaginationResult,
  Reservation,
  ReservationSummary,
  User,
  UserRole,
} from './types';

interface ProfileTabProps {
  myProfile: User | null;
  authEmail: string;
  authRole: UserRole | '';
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
  showCurrentPassword: boolean;
  showNewPassword: boolean;
  showConfirmNewPassword: boolean;
  isUpdatingPassword: boolean;
  setCurrentPassword: (value: string) => void;
  setNewPassword: (value: string) => void;
  setConfirmNewPassword: (value: string) => void;
  toggleShowCurrentPassword: () => void;
  toggleShowNewPassword: () => void;
  toggleShowConfirmNewPassword: () => void;
  onSubmit: (event: FormEvent) => void;
}

export const ProfileTab: React.FC<ProfileTabProps> = ({
  myProfile,
  authEmail,
  authRole,
  currentPassword,
  newPassword,
  confirmNewPassword,
  showCurrentPassword,
  showNewPassword,
  showConfirmNewPassword,
  isUpdatingPassword,
  setCurrentPassword,
  setNewPassword,
  setConfirmNewPassword,
  toggleShowCurrentPassword,
  toggleShowNewPassword,
  toggleShowConfirmNewPassword,
  onSubmit,
}) => {
  return (
    <section className='table-card profile-card'>
      <header>
        <h2>My Profile</h2>
      </header>

      <div className='profile-grid'>
        <div className='profile-item'>
          <p className='profile-label'>Name</p>
          <p>{myProfile?.name || 'N/A'}</p>
        </div>
        <div className='profile-item'>
          <p className='profile-label'>Email</p>
          <p>{myProfile?.email || authEmail || 'N/A'}</p>
        </div>
        <div className='profile-item'>
          <p className='profile-label'>Role</p>
          <p>{myProfile?.role || authRole || 'N/A'}</p>
        </div>
        <div className='profile-item'>
          <p className='profile-label'>Status</p>
          <p>{myProfile?.isActive ? 'Active' : 'Inactive'}</p>
        </div>
      </div>

      <form
        className='profile-password-form'
        onSubmit={onSubmit}>
        <h3>Update Password</h3>
        <label htmlFor='current-password'>Current Password</label>
        <div className='profile-password-wrap'>
          <input
            id='current-password'
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            required
          />
          <button
            type='button'
            className='password-toggle-btn'
            onClick={toggleShowCurrentPassword}>
            {showCurrentPassword ? 'Hide' : 'Show'}
          </button>
        </div>

        <label htmlFor='new-password'>New Password</label>
        <div className='profile-password-wrap'>
          <input
            id='new-password'
            type={showNewPassword ? 'text' : 'password'}
            minLength={8}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
          />
          <button
            type='button'
            className='password-toggle-btn'
            onClick={toggleShowNewPassword}>
            {showNewPassword ? 'Hide' : 'Show'}
          </button>
        </div>

        <label htmlFor='confirm-new-password'>Confirm New Password</label>
        <div className='profile-password-wrap'>
          <input
            id='confirm-new-password'
            type={showConfirmNewPassword ? 'text' : 'password'}
            minLength={8}
            value={confirmNewPassword}
            onChange={(event) => setConfirmNewPassword(event.target.value)}
            required
          />
          <button
            type='button'
            className='password-toggle-btn'
            onClick={toggleShowConfirmNewPassword}>
            {showConfirmNewPassword ? 'Hide' : 'Show'}
          </button>
        </div>

        <button
          type='submit'
          disabled={isUpdatingPassword}>
          {isUpdatingPassword ? 'Updating...' : 'Update Password'}
        </button>
      </form>
    </section>
  );
};

interface ReservationsTabProps {
  summary: ReservationSummary | null;
  trendMax: number;
  nextHourReservations: Reservation[];
  reservations: PaginationResult<Reservation> | null;
  page: number;
  getStatusBadgeClass: (status: Reservation['status']) => string;
  onSelectReservation: (reservationId: string) => void;
  onPageChange: (page: number) => void;
}

export const ReservationsTab: React.FC<ReservationsTabProps> = ({
  summary,
  trendMax,
  nextHourReservations,
  reservations,
  page,
  getStatusBadgeClass,
  onSelectReservation,
  onPageChange,
}) => {
  return (
    <>
      <section className='metric-row'>
        <article className='metric-card'>
          <p>Total this week</p>
          <strong>{summary?.thisWeekCount ?? 0}</strong>
        </article>
        <article className='metric-card'>
          <p>Due today</p>
          <strong>{summary?.todayCount ?? 0}</strong>
        </article>
        <article className='metric-card'>
          <p>Due next hour</p>
          <strong>{summary?.nextHourCount ?? 0}</strong>
        </article>
        <article className='metric-card'>
          <p>Cancel rate</p>
          <strong>{summary?.cancelRate ?? 0}%</strong>
        </article>
      </section>

      <section className='panel-row'>
        <article className='panel-card'>
          <header>
            <h2>Most Popular Days</h2>
          </header>
          <div className='trend-chart'>
            {(summary?.trendLast7Days || []).map((point) => (
              <div
                className='trend-day'
                key={point.date}>
                <div className='trend-bars'>
                  <div
                    className='bar total'
                    style={{
                      height: `${(point.totalCount / trendMax) * 100}%`,
                    }}
                    title={`Total: ${point.totalCount}`}
                  />
                  <div
                    className='bar cancelled'
                    style={{
                      height: `${(point.cancelledCount / trendMax) * 100}%`,
                    }}
                    title={`Cancelled: ${point.cancelledCount}`}
                  />
                </div>
                <p>
                  {new Date(point.date).toLocaleDateString('en-US', {
                    weekday: 'short',
                  })}
                </p>
              </div>
            ))}
          </div>
        </article>
        <article className='panel-card mini'>
          <header>
            <h2>Pay Attention</h2>
          </header>
          <p>Due in the next hour: {nextHourReservations.length}</p>
          {nextHourReservations.length > 0 ? (
            <ul className='service-feed'>
              {nextHourReservations.slice(0, 5).map((reservation) => (
                <li
                  key={reservation.id}
                  className='service-item'>
                  <span className='service-name'>
                    {reservation.customerName}
                  </span>
                  <span className='service-meta'>
                    {reservation.time.slice(0, 5)} ·{' '}
                    {reservation.numberOfGuests} guests
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p>No reservations due in the next hour.</p>
          )}
        </article>
      </section>

      <section className='table-card'>
        <header>
          <h2>Reservations</h2>
        </header>
        <div className='table-wrap'>
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Email</th>
                <th>Date</th>
                <th>Time</th>
                <th>Guests</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {reservations?.items.map((reservation) => (
                <tr key={reservation.id}>
                  <td>{reservation.customerName}</td>
                  <td>{reservation.customerEmail}</td>
                  <td>{reservation.date}</td>
                  <td>{reservation.time.slice(0, 5)}</td>
                  <td>{reservation.numberOfGuests}</td>
                  <td>
                    <span className={getStatusBadgeClass(reservation.status)}>
                      {reservation.status}
                    </span>
                  </td>
                  <td>
                    <button onClick={() => onSelectReservation(reservation.id)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className='pager'>
          <button
            disabled={!reservations?.hasPreviousPage}
            onClick={() => onPageChange(Math.max(1, page - 1))}>
            Previous
          </button>
          <span>
            Page {reservations?.currentPage || 1} of{' '}
            {reservations?.totalPages || 1}
          </span>
          <button
            disabled={!reservations?.hasNextPage}
            onClick={() => onPageChange(page + 1)}>
            Next
          </button>
        </div>
      </section>
    </>
  );
};

interface MessagesTabProps {
  visibleMessages: ContactMessage[];
  messagesPage: number;
  messagesTotalPages: number;
  setMessagesPage: React.Dispatch<React.SetStateAction<number>>;
}

export const MessagesTab: React.FC<MessagesTabProps> = ({
  visibleMessages,
  messagesPage,
  messagesTotalPages,
  setMessagesPage,
}) => {
  return (
    <section className='table-card'>
      <header>
        <h2>Contact Messages</h2>
      </header>

      <div className='table-wrap'>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Message</th>
              <th>Received</th>
            </tr>
          </thead>
          <tbody>
            {visibleMessages.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className='empty-row'>
                  No messages found.
                </td>
              </tr>
            ) : (
              visibleMessages.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.name}</td>
                  <td>{entry.email}</td>
                  <td>{entry.phone}</td>
                  <td className='message-cell'>{entry.message}</td>
                  <td>{new Date(entry.createdAt).toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className='pager'>
        <button
          disabled={messagesPage <= 1}
          onClick={() => setMessagesPage((p) => Math.max(1, p - 1))}>
          Previous
        </button>
        <span>
          Page {messagesPage} of {messagesTotalPages}
        </span>
        <button
          disabled={messagesPage >= messagesTotalPages}
          onClick={() =>
            setMessagesPage((p) => Math.min(messagesTotalPages, p + 1))
          }>
          Next
        </button>
      </div>
    </section>
  );
};

interface UsersTabProps {
  newUser: CreateUserPayload;
  setNewUser: React.Dispatch<React.SetStateAction<CreateUserPayload>>;
  showCreateUserPassword: boolean;
  toggleCreateUserPassword: () => void;
  users: PaginationResult<User> | null;
  usersPage: number;
  setUsersPage: React.Dispatch<React.SetStateAction<number>>;
  handleCreateUser: (event: FormEvent) => void;
  loadUserDetail: (userId: string) => void;
}

export const UsersTab: React.FC<UsersTabProps> = ({
  newUser,
  setNewUser,
  showCreateUserPassword,
  toggleCreateUserPassword,
  users,
  usersPage,
  setUsersPage,
  handleCreateUser,
  loadUserDetail,
}) => {
  return (
    <section className='table-card'>
      <form
        className='create-user-form'
        onSubmit={handleCreateUser}>
        <div className='create-user-head'>
          <h2 className='create-user-title'>Add Team Member</h2>
          <p className='create-user-subtitle'>
            Create a new dashboard account and assign a role.
          </p>
        </div>
        <div className='create-user-grid'>
          <label className='create-user-field'>
            <span>Full Name</span>
            <input
              placeholder='e.g. Jane Doe'
              value={newUser.name}
              onChange={(e) =>
                setNewUser((previous) => ({
                  ...previous,
                  name: e.target.value,
                }))
              }
              required
            />
          </label>
          <label className='create-user-field'>
            <span>Email Address</span>
            <input
              type='email'
              placeholder='name@company.com'
              value={newUser.email}
              onChange={(e) =>
                setNewUser((previous) => ({
                  ...previous,
                  email: e.target.value,
                }))
              }
              required
            />
          </label>
          <label className='create-user-field'>
            <span>Temporary Password</span>
            <div className='create-user-password-wrap'>
              <input
                type={showCreateUserPassword ? 'text' : 'password'}
                placeholder='Set temporary password'
                value={newUser.password}
                onChange={(e) =>
                  setNewUser((previous) => ({
                    ...previous,
                    password: e.target.value,
                  }))
                }
                required
              />
              <button
                type='button'
                className='password-toggle-btn'
                onClick={toggleCreateUserPassword}>
                {showCreateUserPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>
          <label className='create-user-field'>
            <span>Role</span>
            <select
              value={newUser.role}
              onChange={(e) =>
                setNewUser((previous) => ({
                  ...previous,
                  role: e.target.value as UserRole,
                }))
              }>
              <option value='user'>user</option>
              <option value='manager'>manager</option>
              <option value='admin'>admin</option>
            </select>
          </label>
        </div>
        <div className='create-user-actions'>
          <button
            type='submit'
            className='create-user-primary'>
            Add User
          </button>
        </div>
      </form>

      <div className='table-wrap'>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Active</th>
              <th>Deleted</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {users?.items.map((user) => (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>{user.role}</td>
                <td>{user.isActive ? 'Yes' : 'No'}</td>
                <td>{user.isDeleted ? 'Yes' : 'No'}</td>
                <td>
                  <button
                    type='button'
                    onClick={() => loadUserDetail(user.id)}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='pager'>
        <button
          disabled={!users?.hasPreviousPage}
          onClick={() => setUsersPage((p) => Math.max(1, p - 1))}>
          Previous
        </button>
        <span>
          Page {users?.currentPage || 1} of {users?.totalPages || 1}
        </span>
        <button
          disabled={!users?.hasNextPage}
          onClick={() => setUsersPage((p) => p + 1)}>
          Next
        </button>
      </div>
    </section>
  );
};
